import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';
import { jumpTo } from '../lib/jump.js';
import type {
  GeoCheck as GeoCheckRow,
  MatchingData,
  MatchingLink,
  NoFeedDestination,
  UnclaimedFeed,
  UnresolvedLink,
} from '../lib/types.js';
import {
  closestClaimTarget,
  destinationKey,
  preferredClaimTarget,
  registrationKey,
  resolveClaimTarget,
  suggestedKey,
  type SuggestedElsewhere,
} from '../lib/claim-target.js';
import { fmtInt, fmtMoney, todayIso } from '../lib/format.js';
import { accountOption, sourceName } from '../lib/source-name.js';
import { hiddenColumnsNote, visibleColumns } from '../lib/visibleColumns.js';
import {
  compactGeo,
  linkDates,
  linkFactLine,
  toneClass,
  unresolvedFactLine,
  type Fact,
} from '../lib/match-view.js';
import {
  groupLinks,
  groupRegistrations,
  linkableRegistration,
  noFeedReason,
  readyRows,
  registrationHint,
  registrationNumber,
  spendingOnly,
  type FeedGroup,
  type FeedRow,
  type RegistrationGroup,
} from '../lib/match-groups.js';

/**
 * Two pages over one payload, grouped by feed (owner decisions 2026-09-16 and
 * 2026-09-18): money belongs to the feed, decisions belong to the destination.
 * MATCHING lists what can be matched - blocks with a suggested link still to
 * confirm, registrations with a feed to link to - then, apart and uncounted,
 * what waits on a source or on Airman (registrations no source carries,
 * destinations pushed without a feed), the unclaimed feeds with their "Claim
 * for" picker, and every matched feed folded at the bottom for edits (a
 * match made by mistake is undone there). FLAGGED lists the matched feeds
 * that need attention - losing money, spending with no revenue, a warning on
 * a confirmed link - worst net first. Each block is one feed with its money
 * once - earned against spent across every destination - and one row per
 * destination with its verdict. A block can sit on both pages (a losing feed
 * with a suggested link).
 *
 * Confirming/assigning across owners returns a 409 that is surfaced as an
 * inline warning requiring an explicit second click — never a silent attach
 * (H9's UI counterpart). Money here is USD: the server normalises payout with
 * the dashboard's FX join (unrated non-USD rows excluded), so the ranking and
 * the labels agree with every other figure in the app (R32). A link edit
 * moves attributed cost, so the P&L and the buy-side-by-label card are
 * refetched with the profit table (R30).
 */
export type MatchingView = 'matching' | 'flagged';

interface LinkEdit {
  linkId: number;
  action: 'confirm' | 'reject' | 'assign';
  accountId?: string;
  externalId?: string;
  allowCrossOwner?: boolean;
  /** The link's updatedAt as loaded: the server refuses an edit made since (409 stale). */
  updatedAt?: string;
}

/** Claim a feed for a destination that has no open link - there is no
 * registration to assign, so the server creates the confirmed link. */
interface ClaimAction {
  action: 'claim';
  destinationId: string;
  accountId: string;
  externalId: string;
  allowCrossOwner?: boolean;
}

type LinkAction = LinkEdit | ClaimAction;

interface PendingCrossOwner {
  action: LinkAction;
  message: string;
}

/**
 * The Unclaimed feeds table as a column list: `get` is the raw value the
 * owner's hide-the-column rule reads (a null geo, a zero count), `cell` the
 * rendering. Feed, Rep and Provider are the row's identity and never hide. A
 * sheet, upload or inbox source is named by its own label, never "generic"
 * (owner question 2026-09-18).
 */
const UNCLAIMED_COLUMNS: {
  key: string;
  cls?: string | undefined;
  fixed?: boolean | undefined;
  get: (u: UnclaimedFeed) => unknown;
  cell: (u: UnclaimedFeed) => ReactNode;
}[] = [
  {
    key: 'Feed',
    cls: 'l',
    fixed: true,
    get: (u) => u.feedId,
    cell: (u) => (
      <>
        {u.feedId} <span className="count">({u.accountId})</span>
      </>
    ),
  },
  { key: 'Rep', cls: 'l', fixed: true, get: (u) => u.rep, cell: (u) => u.rep },
  {
    key: 'Provider',
    cls: 'l',
    fixed: true,
    get: (u) => u.provider,
    cell: (u) =>
      sourceName({ provider: u.provider, providerLabel: u.providerLabel, label: u.accountLabel }),
  },
  { key: 'Geo', cls: 'l', get: (u) => u.geo, cell: (u) => u.geo ?? '—' },
  { key: 'Searches 14d', get: (u) => u.searches14d, cell: (u) => fmtInt(u.searches14d) },
  {
    key: 'Revenue 14d (USD)',
    cls: 'pay',
    get: (u) => u.payout14d,
    cell: (u) => fmtMoney(u.payout14d),
  },
  { key: 'Last day', cls: 'l dim', get: (u) => u.lastDay, cell: (u) => u.lastDay ?? '—' },
];

/**
 * The destination's page in Airman - where it is paused, lowered or switched
 * off (owner ask 2026-09-18: find and turn off what is unprofitable). Only
 * once the Airman base URL is set under APIs.
 */
function airmanLink(base: string | null, destinationId: string): ReactNode {
  if (!base) return null;
  return (
    <a
      className="airman-link"
      href={`${base}/destinations/${encodeURIComponent(destinationId)}/config`}
      target="_blank"
      rel="noreferrer"
      title="open this destination in Airman - pause it, lower its volume or switch it off there"
    >
      Airman ↗
    </a>
  );
}

export function MatchingPage({ view = 'matching' }: { view?: MatchingView }) {
  const qc = useQueryClient();
  const matching = useQuery({
    queryKey: ['matching'],
    queryFn: () => api.get<MatchingData>('/api/matching'),
  });
  const [crossOwner, setCrossOwner] = useState<PendingCrossOwner | null>(null);
  const [error, setError] = useState('');

  const act = useMutation({
    mutationFn: (a: LinkAction) =>
      api.post(a.action === 'claim' ? '/api/matching/claim' : '/api/matching/link', a),
    onSuccess: () => {
      setCrossOwner(null);
      setError('');
      void qc.invalidateQueries({ queryKey: ['matching'] });
      // The header's counts and their items follow the same data: refreshed now, not at the next minute.
      void qc.invalidateQueries({ queryKey: ['attention'] });
      void qc.invalidateQueries({ queryKey: ['summary'] });
      void qc.invalidateQueries({ queryKey: ['profit'] });
      void qc.invalidateQueries({ queryKey: ['pl'] });
      void qc.invalidateQueries({ queryKey: ['taxonomy'] });
    },
    onError: (err, action) => {
      if (err instanceof ApiError && err.status === 409 && err.body.crossOwner) {
        setCrossOwner({ action, message: err.message });
        return;
      }
      if (err instanceof ApiError && err.status === 409 && err.body.stale) {
        // Someone else edited this link first: show why, and reload the page data.
        setCrossOwner(null);
        void qc.invalidateQueries({ queryKey: ['matching'] });
      }
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const [confirmingAll, setConfirmingAll] = useState(false);

  const m = matching.data;
  // /matching#feed-<id> or /flagged#feed-<id> (the Stats page's drill-down):
  // jump to that feed's block and mark it once the data is in.
  useEffect(() => {
    if (!m) return;
    const hash = window.location.hash;
    if (!hash.startsWith('#feed-')) return;
    const el = document.querySelector<HTMLElement>(
      `[data-feed="${CSS.escape(hash.slice('#feed-'.length))}"]`,
    );
    if (!el) return;
    // A matched block sits inside the folded list: open it, or the jump lands on nothing.
    const fold = el.closest('details');
    if (fold) fold.open = true;
    jumpTo(el, 'center');
    el.classList.add('target');
  }, [m]);
  if (matching.isLoading) return <p className="kicker">Loading matching data…</p>;
  if (matching.isError || !m) return <div className="warnbox">Could not load matching data.</div>;

  const today = todayIso();
  const groups = groupLinks(m.links, today);
  // Matching: a block with a row still to confirm. Flagged: a matched feed
  // losing money, spending with no revenue, or with a warning on a confirmed
  // row - worst net first. Matched: every feed whose rows are all confirmed,
  // folded on Matching for edits (a block with a row still to confirm is
  // already above, its confirmed rows editable there - never twice on one
  // page). History: nothing but closed links.
  const unmatched = groups.filter((g) => g.toConfirm > 0);
  const flagged = groups.filter((g) => g.flagged && !g.historyOnly).sort((a, b) => a.net - b.net);
  const matchedAll = groups.filter((g) => !g.historyOnly && g.toConfirm === 0 && g.rows.length > 0);
  const history = groups.filter((g) => g.historyOnly);
  const ready = readyRows(unmatched);
  // Registrations and feed-less destinations are listed only while they spend
  // (owner ask 2026-09-15); the rest are counted and stay claimable from the
  // feed side. Only an ACTIVE destination with no open link is listed: a
  // stopped one is offered to the claim picker, never listed (2026-09-18).
  const openShown = spendingOnly(m.unresolved);
  const noFeedActive = m.noFeed.filter((d) => d.status === 'active');
  const noFeedShown = spendingOnly(noFeedActive);
  const quiet = m.unresolved.length - openShown.length + (noFeedActive.length - noFeedShown.length);
  // A registration with a feed to link to can be matched here; one no source
  // carries waits on a source or on Airman, like a destination pushed without
  // a feed - listed apart, never counted (owner 2026-09-18: "13 to match but
  // I can only see 1").
  const linkable = openShown.filter(linkableRegistration);
  const waitingRegs = openShown.filter((u) => !linkableRegistration(u));
  const registrations = groupRegistrations(linkable);
  const waiting = groupRegistrations(waitingRegs);
  const waitingCount = waitingRegs.length + noFeedShown.length;
  const toConfirm = unmatched.reduce((n, g) => n + g.toConfirm, 0);
  // The header's Matching count: every row a click matches - a suggested link,
  // a registration with a source. A block is one feed and can hold several
  // rows; an unclaimed feed is not a decision until a destination for it exists.
  const decisionRows = toConfirm + linkable.length;
  const losingFeeds = flagged.filter((g) => g.losing);
  const lost = losingFeeds.reduce((n, g) => n - g.netConfirmed, 0);
  const quietFeeds = flagged.filter((g) => g.noRevenue);
  const quietSpend = quietFeeds.reduce((n, g) => n + g.spent7d, 0);
  const warnedOnly = flagged.length - losingFeeds.length - quietFeeds.length;
  // The header's Flagged count is the money problems; a warning is listed, not counted.
  const flaggedCount = losingFeeds.length + quietFeeds.length;
  // Destinations whose link is only suggested, to another feed: the claim picker can move them (2026-09-18).
  const suggestedElsewhere: SuggestedElsewhere[] = groups.flatMap((g) =>
    g.rows.filter((r) => r.link.status === 'provisional').map((r) => r.link),
  );
  // A feed with neither a search nor a cent in 14 days is not worth a row (review 2026-09-18): counted, not listed.
  const unclaimedShown = m.unclaimed.filter((u) => u.payout14d > 0 || u.searches14d > 0);
  const unclaimedQuiet = m.unclaimed.length - unclaimedShown.length;
  const unclaimedCols = visibleColumns(unclaimedShown, UNCLAIMED_COLUMNS);
  // Anything an unclaimed feed can be claimed for: an open registration, a
  // destination with no open link - active or stopped - or one suggested elsewhere.
  // A destination whose suggested link is still a row is offered as that suggestion only (Match
  // moves it, spend and all) - never also as a "links ended" destination (review 2026-09-18).
  const suggestedDestIds = new Set(suggestedElsewhere.map((l) => l.destinationId));
  const freeDestinations = m.noFeed.filter((d) => !suggestedDestIds.has(d.destinationId));
  const knownReps = new Set(m.accounts.map((a) => a.rep.toLowerCase()));
  const claimable = m.unresolved.length + freeDestinations.length + suggestedElsewhere.length > 0;
  // Exactly the rows showing Ready, through the same confirm action, one
  // request each in order - never anything amber or red.
  const confirmAllReady = async () => {
    setConfirmingAll(true);
    try {
      for (const l of ready)
        await act.mutateAsync({ linkId: l.id, action: 'confirm', updatedAt: l.updatedAt });
    } catch {
      // act.onError has already shown the message and refetched.
    } finally {
      setConfirmingAll(false);
    }
  };
  const run = (a: LinkAction) => act.mutate(a);
  const airman = m.airmanBase;
  const block = (g: FeedGroup) => (
    <FeedBlock key={g.key} g={g} accounts={m.accounts} act={run} airman={airman} />
  );

  return (
    <div>
      <div className="pagehead">
        <h1>{view === 'flagged' ? 'Flagged' : 'Matching'}</h1>
      </div>
      {error ? <div className="warnbox">{error}</div> : null}
      {crossOwner ? (
        <div className="warnbox">
          <p style={{ margin: '0 0 8px' }}>{crossOwner.message}</p>
          <div className="match-actions">
            <button
              className="small"
              onClick={() => act.mutate({ ...crossOwner.action, allowCrossOwner: true })}
            >
              Link anyway
            </button>
            <button className="ghost small" onClick={() => setCrossOwner(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {view === 'flagged' ? (
        <section className="m-sec" data-section="flagged">
          <div className="sec-head">
            <h2>Needs attention</h2>
            <span
              className="count"
              title="the header's Flagged count: feeds losing money or spending with no revenue on confirmed links; a warning on a matched link is listed, not counted"
            >
              {flaggedCount
                ? `${flaggedCount} feed${flaggedCount === 1 ? '' : 's'}`
                : 'nothing losing money'}
              {losingFeeds.length
                ? ` · ${losingFeeds.length} losing (−${fmtMoney(lost)} net in 7 days)`
                : ''}
              {quietFeeds.length
                ? ` · ${quietFeeds.length} spending with no revenue (${fmtMoney(quietSpend)} in 7 days)`
                : ''}
              {warnedOnly ? ` · ${warnedOnly} with a warning` : ''}
            </span>
          </div>
          {flagged.length === 0 ? (
            <p className="kicker">
              Every matched feed that is sending traffic earns more than it spends, on the countries
              it declares. Every matched feed is listed under Matching, folded at the bottom.
            </p>
          ) : (
            flagged.map(block)
          )}
        </section>
      ) : (
        <>
          <section className="m-sec" data-section="unmatched">
            <div className="sec-head">
              <h2>Unmatched</h2>
              <span
                className="count"
                title="the header's Matching count: every row a click matches - a suggested link, a registration with a source to link; a block is one feed and can hold several. What waits on a source or on Airman is listed below, not counted"
              >
                {decisionRows} to match
                {toConfirm
                  ? ` · ${toConfirm} suggested link${toConfirm === 1 ? '' : 's'} to confirm`
                  : ''}
                {linkable.length
                  ? ` · ${linkable.length} registration${linkable.length === 1 ? '' : 's'} with a source to link`
                  : ''}
                {waitingCount
                  ? ` · ${waitingCount} waiting on a source or on Airman (not counted)`
                  : ''}
                {m.unresolvedTotal > m.unresolved.length || m.noFeedTotal > m.noFeed.length
                  ? ' (of more - newest shown)'
                  : ''}
                {quiet ? ` · ${quiet} with no spend in 14 days not listed` : ''}
              </span>
            </div>
            {decisionRows === 0 ? (
              <p className="kicker">
                {waitingCount
                  ? 'Nothing to match right now - what is listed below waits on a source or on Airman.'
                  : quiet
                    ? `Nothing spending waits — ${quiet} with no spend in the last 14 days ${quiet === 1 ? 'is' : 'are'} not listed (still offered under Claim for).`
                    : 'Nothing unmatched — every pushed destination is linked.'}
              </p>
            ) : (
              <>
                {ready.length > 0 ? (
                  <div className="match-bulk">
                    <button
                      className="small"
                      disabled={confirmingAll}
                      onClick={() => void confirmAllReady()}
                    >
                      {confirmingAll ? 'Confirming…' : `Confirm all ready (${ready.length})`}
                    </button>
                    <span className="count">
                      every row marked Ready below - nothing amber or red
                    </span>
                  </div>
                ) : null}
                {unmatched.map(block)}
                {registrations.map((g) => (
                  <RegistrationBlock key={g.key} g={g} act={run} airman={airman} />
                ))}
              </>
            )}
            {waitingCount ? (
              <div className="m-waiting" data-waiting>
                <div className="sec-head">
                  <h3>Waiting on a source or on Airman</h3>
                  <span
                    className="count"
                    title="nothing to match these to yet: no stats source reports the feed number, or Airman pushed the destination without one - not counted above"
                  >
                    {waitingRegs.length
                      ? `${waitingRegs.length} registration${waitingRegs.length === 1 ? '' : 's'} no source carries`
                      : ''}
                    {waitingRegs.length && noFeedShown.length ? ' · ' : ''}
                    {noFeedShown.length
                      ? `${noFeedShown.length} destination${noFeedShown.length === 1 ? '' : 's'} with no feed`
                      : ''}
                    {
                      ' · add the source under Configuration → Add stats, or fix the channel in Airman'
                    }
                  </span>
                </div>
                {waiting.map((g) => (
                  <RegistrationBlock key={g.key} g={g} act={run} airman={airman} />
                ))}
                {noFeedShown.length ? (
                  <section className="feed-group" data-nofeed-list>
                    <div className="feed-head">
                      <span className="feed-id">
                        <small>no feed</small>
                        {noFeedShown.length} destination{noFeedShown.length === 1 ? '' : 's'}
                      </span>
                      <span className="feed-facts">
                        <span>active and spending, with nothing to match on</span>
                      </span>
                    </div>
                    {noFeedShown.map((d) => (
                      <NoFeedRow key={d.destinationId} d={d} airman={airman} />
                    ))}
                  </section>
                ) : null}
              </div>
            ) : null}

            <div className="table-card" data-unclaimed>
              <div className="table-head">
                <h3>Unclaimed feeds</h3>
                <span className="count">
                  {unclaimedShown.length} with stats and no destination
                  {unclaimedQuiet
                    ? ` · ${unclaimedQuiet} with no searches or revenue in 14 days not listed`
                    : ''}
                  {m.unclaimedTotal > m.unclaimed.length
                    ? ` (of ${m.unclaimedTotal}, newest shown)`
                    : ''}
                </span>
              </div>
              {unclaimedShown.length === 0 ? (
                <p className="kicker">Every feed with recent stats is claimed by a destination.</p>
              ) : (
                <div className="table-scroll">
                  <table className="stat-table">
                    <thead>
                      <tr>
                        {unclaimedCols.shown.map((c) => (
                          <th key={c.key} className={c.cls}>
                            {c.key}
                          </th>
                        ))}
                        {claimable ? <th className="l">Claim for</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {unclaimedShown.map((u) => (
                        <tr key={`${u.accountId}:${u.feedId}`}>
                          {unclaimedCols.shown.map((c) => (
                            <td key={c.key} className={c.cls}>
                              {c.cell(u)}
                            </td>
                          ))}
                          {claimable ? (
                            <td className="l">
                              <ClaimCell
                                u={u}
                                open={m.unresolved}
                                free={freeDestinations}
                                suggested={suggestedElsewhere}
                                knownReps={knownReps}
                                act={run}
                              />
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="table-note">
                {claimable
                  ? 'Feeds with stats in the last 14 days that no destination claims. The picker opens on the closest match in Airman - the registration that pushed this feed number, else the destination whose channel, URL or name carries it - and says why; every other choice is listed too: open registrations, destinations with no open link (active, and the ones stopped in Airman: no feed there, unlinked, or links that ended), and destinations whose link is only suggested, to another feed. Pick the right one and Match: the link is confirmed by you, here. A destination already confirmed to another feed is edited from its block under All matched feeds below.'
                  : 'Feeds with stats in the last 14 days that no destination claims. Nothing to claim them for yet: every destination is linked. A new registration from the buy side, or a destination Airman reports without a feed, will appear here as a choice.'}
                {unclaimedCols.hidden.length ? ` ${hiddenColumnsNote(unclaimedCols.hidden)}` : ''}
              </div>
            </div>
          </section>

          <section className="m-sec" data-section="matched">
            <details className="m-fold m-clean">
              <summary>
                <h2>All matched feeds</h2>
                <span className="count">
                  {matchedAll.length} feed{matchedAll.length === 1 ? '' : 's'}
                  {history.length ? ` · ${history.length} with only closed links` : ''}
                  {m.linksTotal > m.links.length
                    ? ` · of ${m.linksTotal} links (newest shown)`
                    : ''}
                  {' · open to edit or unlink a match made by mistake'}
                </span>
              </summary>
              {matchedAll.length === 0 && history.length === 0 ? (
                <p className="kicker">No feed is confirmed yet.</p>
              ) : (
                matchedAll.map(block)
              )}
              {history.length ? (
                <details className="m-fold" data-history>
                  <summary className="count">
                    {history.length} feed{history.length === 1 ? '' : 's'} with only closed links —
                    windows that ended; the spend of those days stays attributed
                  </summary>
                  {history.map(block)}
                </details>
              ) : null}
            </details>
          </section>
        </>
      )}
    </div>
  );
}

/** The feed's countries on the block header: its dominant one and how many more. */
function feedGeos(g: FeedGroup): string {
  if (!g.feedGeo) return 'no country reported';
  return g.feedGeoCount > 1 ? `${g.feedGeo} +${g.feedGeoCount - 1} geos` : g.feedGeo;
}

/**
 * One feed: the header with its facts and money once, a row per destination,
 * the closed windows folded, and a note when it spends without earning.
 */
function FeedBlock({
  g,
  accounts,
  act,
  airman,
}: {
  g: FeedGroup;
  accounts: MatchingData['accounts'];
  act: (a: LinkAction) => void;
  airman: string | null;
}) {
  const state =
    g.toConfirm && g.toCheck
      ? `${g.toConfirm} to confirm · ${g.toCheck} to check`
      : g.toConfirm
        ? `${g.toConfirm} to confirm`
        : g.toCheck
          ? `${g.toCheck} to check`
          : 'Confirmed';
  return (
    <section
      className={'feed-group' + (g.noRevenue || g.losing ? ' alert' : '')}
      data-feed={g.feedId}
      data-account={g.accountId ?? ''}
    >
      <div className="feed-head">
        <span className="feed-id" title="the feed id the stats carry">
          <small>feed</small>
          {g.feedId}
        </span>
        <span className="feed-facts">
          <span title="the source that reports this feed">{g.source}</span>
          <span title="the rep who owns the source">{g.rep ?? '?'}</span>
          <span title="the feed's type (Airman's network when not yet set)">
            {g.type ?? 'unclassified'}
          </span>
          <span title="the countries the feed reports">{feedGeos(g)}</span>
        </span>
        <span className="feed-money">
          <span title="USD payout on this feed over the last 7 days">
            earned <b>{fmtMoney(g.earned7d)}</b> (7d)
          </span>
          <span title="Airman spend over the last 7 days attributed to this feed: every destination row below, and the days of links that ended inside the week">
            spent <b>{fmtMoney(g.spent7d)}</b> on {g.rows.length} destination
            {g.rows.length === 1 ? '' : 's'}
            {g.spentSuggested7d > 0 && g.spentSuggested7d < g.spent7d
              ? ` (${fmtMoney(g.spentSuggested7d)} on suggested links)`
              : ''}
            {g.spentClosed7d > 0 ? ` (${fmtMoney(g.spentClosed7d)} of it on links that ended)` : ''}
          </span>
          <span
            className={'net ' + (g.net < 0 ? 'neg' : 'pos')}
            title="earned minus spent, 7 days - and as a share of the spend"
          >
            net {g.net < 0 ? '−' : '+'}
            {fmtMoney(Math.abs(g.net))}
            {g.margin != null ? (
              <small className="margin">{` (${g.margin < 0 ? '−' : '+'}${Math.abs(g.margin)}%)`}</small>
            ) : null}
          </span>
          {g.unrated ? (
            <span
              className="pill"
              data-state="unrated"
              title="a non-USD row in the last 7 days has no rate yet: earned is understated, so no verdict until it is rated"
            >
              Unrated rows (7d)
            </span>
          ) : g.noRevenue ? (
            <span className="pill warn" data-state="no-revenue">
              {g.lastDay ? `No revenue since ${g.lastDay}` : 'No revenue'}
            </span>
          ) : g.losing ? (
            <span
              className="pill warn"
              data-state="losing"
              title="earning less than it spends over the last 7 days, and still being sent traffic"
            >
              Losing {fmtMoney(-g.netConfirmed)} (7d)
            </span>
          ) : g.stoppedQuiet ? (
            <span
              className="pill"
              data-state="stopped"
              title="no destination has spent on this feed in the last two days"
            >
              {g.lastSpendDay ? `Stopped ${g.lastSpendDay}` : 'Stopped'}
            </span>
          ) : (
            <span className={g.needsDecision ? 'pill gold' : 'pill done'} data-state={state}>
              {state}
            </span>
          )}
        </span>
      </div>
      {g.rows.map((r) => (
        <LinkRow key={r.link.id} row={r} accounts={accounts} act={act} airman={airman} />
      ))}
      {g.closed.length ? (
        <details className="feed-fold">
          <summary>
            {g.closed.length} closed link{g.closed.length === 1 ? '' : 's'} — windows that ended;
            the spend of those days stays attributed
          </summary>
          {g.closed.map((l) => (
            <LinkRow
              key={l.id}
              row={{
                link: l,
                verdict: { label: 'Confirmed', tone: 'ok', order: 0 },
                reason: null,
                note: `until ${l.validTo}`,
                urlFeed: null,
                geo: compactGeo(l.geo, l.geoMix),
              }}
              accounts={accounts}
              act={act}
              airman={airman}
              closed
            />
          ))}
        </details>
      ) : null}
      {g.noRevenue ? (
        <p className="feed-note">
          <b>Spending with no revenue.</b> Check the feed's source under Providers
          {g.lastDay ? ` (its last stats day is ${g.lastDay})` : ''}, and the channel and URL on the
          destinations above.
        </p>
      ) : g.losing ? (
        <p className="feed-note">
          <b>Losing money.</b> Earned {fmtMoney(g.earned7d)} against {fmtMoney(g.spentConfirmed7d)}{' '}
          spent on confirmed links in the last 7 days
          {g.lastDay && g.lastDay < todayIso()
            ? ` (stats through ${g.lastDay} - later days' revenue has not landed yet)`
            : ''}
          . Pause or lower the destinations above in Airman, or check the feed's payout with its
          source.
        </p>
      ) : g.stoppedQuiet ? (
        <p className="feed-note muted-note">
          Stopped: nothing has been sent since {g.lastSpendDay}, and the feed reported nothing for
          those last days. Nothing to do unless it runs again.
        </p>
      ) : null}
    </section>
  );
}

/**
 * One destination on a feed: the Airman id and name, its geo, its own visits
 * and spend, the verdict with its one reason, the actions - and the seven
 * checks behind "details".
 */
function LinkRow({
  row,
  accounts,
  act,
  airman,
  closed = false,
}: {
  row: FeedRow;
  accounts: MatchingData['accounts'];
  act: (a: LinkAction) => void;
  airman: string | null;
  closed?: boolean;
}) {
  const { link, verdict } = row;
  const [editing, setEditing] = useState(false);
  const [accountId, setAccountId] = useState(link.accountId ?? '');
  const [externalId, setExternalId] = useState(link.feedId);
  const confirmable = link.status !== 'confirmed';
  const dates = linkDates(link);
  return (
    <div className="match-card" data-feed-row={link.feedId} data-link={link.id} title={dates}>
      <div className="match-head">
        <code title="Airman destination id">{link.destinationId}</code>
        <span className="name">{link.destinationName ?? '—'}</span>
      </div>
      <div
        className="row-geo"
        title="the device and the countries the destination declares; served = the feed carries searches for them"
      >
        {link.device ? `${link.device} · ` : ''}
        {row.geo}
      </div>
      <div
        className="row-num"
        title="visits Airman committed to this destination over the last 7 days"
      >
        {fmtInt(link.buyCommitted7d)}
        <small>visits 7d</small>
      </div>
      <div className="row-num" title="Airman spend on this destination over the last 7 days">
        {fmtMoney(link.buyCost7d)}
        <small>spent 7d</small>
      </div>
      <div className="row-verdict">
        <span
          className={closed ? 'pill' : toneClass(verdict.tone)}
          data-verdict={closed ? 'Closed' : verdict.label}
          title={
            verdict.label === 'Confirmed'
              ? link.autoConfirm
                ? autoConfirmTip(link.autoConfirm)
                : 'confirmed by a person'
              : verdict.label === 'Ready'
                ? 'every check passes'
                : verdict.label
          }
        >
          {closed ? 'Closed' : verdict.label === 'Confirmed' && link.autoConfirm ? 'Auto-confirmed' : verdict.label}
        </span>
        {link.status === 'provisional' && link.autoFlag?.kind === 'no_delivery' ? (
          <span
            className="reason bad"
            title="the daily sweep saw this feed report on days this destination sent nothing: most likely someone else's traffic - reject the link unless you know better"
          >
            no delivery: the feed reported {link.autoFlag.feedDays ?? 'some'} days, we sent none
          </span>
        ) : null}
        {row.reason ? (
          <span className={'reason' + (verdict.tone === 'error' ? ' bad' : '')}>{row.reason}</span>
        ) : null}
        {row.note ? <span className="reason">{row.note}</span> : null}
      </div>
      <div className="match-actions">
        {row.urlFeed && link.accountId ? (
          <button
            className="small"
            title="link this destination to the feed its URL names instead"
            onClick={() =>
              act({
                linkId: link.id,
                action: 'assign',
                accountId: link.accountId!,
                externalId: row.urlFeed!,
                updatedAt: link.updatedAt,
              })
            }
          >
            Link to {row.urlFeed}
          </button>
        ) : null}
        {confirmable ? (
          <button
            className="small"
            onClick={() => act({ linkId: link.id, action: 'confirm', updatedAt: link.updatedAt })}
          >
            Confirm
          </button>
        ) : null}
        <button className="ghost small" onClick={() => setEditing(!editing)}>
          {editing ? 'Close' : 'Edit'}
        </button>
        <button
          className="ghost small"
          onClick={() => act({ linkId: link.id, action: 'reject', updatedAt: link.updatedAt })}
        >
          {confirmable ? 'Reject' : 'Unlink'}
        </button>
        {airmanLink(airman, link.destinationId)}
      </div>
      <details className="match-more">
        <summary>details</summary>
        <div className="match-facts">
          <FactLine facts={linkFactLine(link)} />
          <span className="fact plain" title="the link's window">
            <span className="k">window</span> <span className="v">{dates}</span>
          </span>
        </div>
      </details>
      {editing ? (
        <div className="sugg">
          <span className="count">{dates}</span>
          <span className="match-actions">
            <select
              className="chip"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              <option value="">Pick account…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {accountOption(a)} ({a.id})
                </option>
              ))}
            </select>
            <input
              style={{ width: 120 }}
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder="feed id"
              aria-label="feed id"
            />
            <button
              className="small"
              disabled={!accountId || !externalId}
              onClick={() =>
                act({
                  linkId: link.id,
                  action: 'assign',
                  accountId,
                  externalId,
                  updatedAt: link.updatedAt,
                })
              }
            >
              Save link
            </button>
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Open registrations that wait on one feed number no account carries: the
 * header names the number and what Airman pushed it as, each row is a
 * destination with its 14-day spend, and the candidates - by the channel, or
 * by the URL's id when the channel finds none - carry their own Link button.
 */
function RegistrationBlock({
  g,
  act,
  airman,
}: {
  g: RegistrationGroup;
  act: (a: LinkAction) => void;
  airman: string | null;
}) {
  const first = g.rows[0]!;
  return (
    <section className="feed-group" data-feed={g.feedId} data-registration>
      <div className="feed-head">
        <span className="feed-id" title="the feed number Airman's registration carries">
          <small>feed</small>
          {g.feedId}
        </span>
        <span className="feed-facts">
          <span title="the provider Airman pushed the feed as">{first.feedProvider}</span>
          <span title="the relationship as Airman names it, else the owner">
            {first.relationship ?? first.ownerRep ?? first.owner ?? '?'}
          </span>
          <span title="Airman's network - the suggested feed type">{first.network ?? '?'}</span>
        </span>
        <span className="feed-money">
          <span title="Airman spend over the last 14 days across these registrations">
            spent <b>{fmtMoney(g.spent14d)}</b> on {g.rows.length} destination
            {g.rows.length === 1 ? '' : 's'} (14d)
          </span>
          <span
            className={g.candidates ? 'pill gold' : 'pill warn'}
            data-state={g.candidates ? 'candidates' : 'no-candidates'}
          >
            {g.candidates
              ? `${g.candidates} candidate${g.candidates === 1 ? '' : 's'}`
              : 'No source carries it'}
          </span>
        </span>
      </div>
      {g.rows.map((u) => (
        <RegistrationRow key={u.id} u={u} act={act} airman={airman} />
      ))}
    </section>
  );
}

function RegistrationRow({
  u,
  act,
  airman,
}: {
  u: UnresolvedLink;
  act: (a: LinkAction) => void;
  airman: string | null;
}) {
  const urlNote =
    u.urlAgrees === false
      ? `URL says ${u.urlFeedId ?? u.urlIds.map((i) => i.value).join(' / ')}; the channel is ${u.channel ?? u.feedId} - the provider reports under the URL's id, so correct the channel in Airman${u.suggestions.some((s) => s.via === 'url') ? ', or link the feed the URL names below' : ''}`
      : u.channelSource === 'url'
        ? `no channel in Airman; the URL names feed ${u.channel ?? u.feedId}`
        : u.urlAgrees === true
          ? 'the URL agrees with the channel'
          : null;
  const chip =
    u.confidence === 'high' ? 'pill ok' : u.confidence === 'low' ? 'pill gold' : 'pill warn';
  const label = u.confidence === 'none' ? 'No candidates' : `${u.confidence} confidence`;
  return (
    <div
      className="match-card"
      data-registration-row={u.id}
      title={`registration created ${u.createdAt.slice(0, 10)}`}
    >
      <div className="match-head">
        <code title="Airman destination id">{u.destinationId}</code>
        <span className="name">{u.destinationName ?? '—'}</span>
      </div>
      <div className="row-geo">
        {u.device ? `${u.device} · ` : ''}
        {compactGeo(u.geo, u.geoMix)}
      </div>
      <div
        className="row-num"
        title="visits Airman committed to this destination over the last 14 days"
      >
        {fmtInt(u.buyCommitted14d)}
        <small>visits 14d</small>
      </div>
      <div className="row-num" title="Airman spend on this destination over the last 14 days">
        {fmtMoney(u.buyCost14d)}
        <small>spent 14d</small>
      </div>
      <div className="row-verdict">
        <span className={chip} data-verdict={label}>
          {label}
        </span>
        {urlNote ? (
          <span className={'reason' + (u.urlAgrees === false ? ' bad' : '')}>{urlNote}</span>
        ) : null}
        {u.channelSource === 'channel' && u.channel && u.channel !== u.feedId ? (
          <span
            className="reason"
            title="the registration was pushed under this number; Airman's channel was corrected since, and the block is matched on the channel as it is now"
          >
            registered as “{u.feedId}”, the channel is {u.channel} now
          </span>
        ) : null}
        {registrationHint(u) ? <span className="reason">{registrationHint(u)}</span> : null}
      </div>
      <div className="match-actions">{airmanLink(airman, u.destinationId)}</div>
      <details className="match-more">
        <summary>details</summary>
        <div className="match-facts">
          <FactLine facts={unresolvedFactLine(u)} />
        </div>
      </details>
      {u.suggestions.map((s) => (
        <div className="sugg" key={`${s.accountId}:${s.feedId}`}>
          <span>
            {s.rep} @{' '}
            {sourceName({
              provider: s.provider,
              providerLabel: s.providerLabel,
              label: s.accountLabel,
            })}{' '}
            <span className="count">({s.accountId})</span>{' '}
            <span
              className="count"
              title="the feed's classified type (Airman's network is the suggestion)"
            >
              {s.feedType ?? 'unclassified'}
            </span>{' '}
            <b className="geo" title="feed's dominant geo (— = no country reported)">
              {s.geo ?? '—'}
            </b>
            {s.geoCount > 1 ? <span className="count">+{s.geoCount - 1} geos</span> : null}
            {' · '}payout 14d (USD) {fmtMoney(s.payout14d)}
            {s.lastDay ? ` · last ${s.lastDay}` : ' · no recent stats'}
          </span>
          <span className="match-actions">
            {s.ownerMatch ? (
              <span className="pill ok">owner’s account</span>
            ) : !s.sameRep && u.ownerKnown ? (
              <span
                className="pill warn"
                title="this feed number under another rep's account - one provider issues the same number to several people; the owner's own source is what is missing"
              >
                another rep’s account
              </span>
            ) : null}
            {s.via === 'url' ? (
              <span
                className="pill gold"
                title="found by the id in the destination’s URL - no source carries the channel’s number"
              >
                by URL id {s.feedId}
              </span>
            ) : s.feedId !== registrationNumber(u) ? (
              <span
                className="pill gold"
                title="the source reports the feed under this id - the same digits as the channel, without its prefix"
              >
                as feed {s.feedId}
              </span>
            ) : null}
            <GeoCheck checks={s.geoChecks} total={s.feedTotalSearches} feedGeo={s.geo} />
            <button
              className="small"
              onClick={() =>
                act({
                  linkId: u.id,
                  action: 'assign',
                  accountId: s.accountId,
                  externalId: s.feedId,
                  updatedAt: u.updatedAt,
                })
              }
            >
              Link
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * A destination that is active and spending with nothing to match on: no
 * channel any source carries, and no id in its URL. Its fix is in Airman, or
 * a claim from the feed side; it must be visible either way.
 */
function NoFeedRow({ d, airman }: { d: NoFeedDestination; airman: string | null }) {
  return (
    <div
      className="match-card"
      data-nofeed={d.destinationId}
      title={`first seen ${d.firstSeen.slice(0, 10)}`}
    >
      <div className="match-head">
        <code title="Airman destination id">{d.destinationId}</code>
        <span className="name">{d.destinationName ?? '—'}</span>
      </div>
      <div className="row-geo">{d.geo ?? 'any'}</div>
      <div className="row-num" />
      <div className="row-num" title="Airman spend on this destination over the last 14 days">
        {fmtMoney(d.buyCost14d)}
        <small>spent 14d</small>
      </div>
      <div className="row-verdict">
        <span
          className="pill warn"
          data-verdict={
            d.reason === 'unlinked' ? 'Unlinked' : d.reason === 'closed' ? 'Links ended' : 'No feed'
          }
        >
          {d.reason === 'unlinked' ? 'Unlinked' : d.reason === 'closed' ? 'Links ended' : 'No feed'}
        </span>
        <span className="reason">
          {d.channel ? `channel ${d.channel} · ` : ''}
          {noFeedReason(d)}
        </span>
      </div>
      <div className="match-actions">{airmanLink(airman, d.destinationId)}</div>
    </div>
  );
}

/**
 * Claim an unclaimed feed for a destination. Three kinds of target: an open
 * registration (Match reuses `assign`, so the cross-owner guard applies), a
 * destination with no OPEN link - active, or stopped in Airman: one Airman
 * reports without a feed, one whose links were unlinked, or whose windows all
 * ended - for which Match creates the confirmed link (`claim`, the same
 * guard), and a destination whose link is only suggested, to another feed,
 * which Match moves here (`assign`). The picker opens on the closest match in
 * Airman and says why (lib/claim-target.ts: the registration that pushed this
 * feed number, else the entry whose channel, URL or name carries it); a guess
 * only ever preselects, the person confirms by pressing Match.
 */
function ClaimCell({
  u,
  open,
  free,
  suggested,
  knownReps,
  act,
}: {
  u: UnclaimedFeed;
  open: UnresolvedLink[];
  free: NoFeedDestination[];
  suggested: SuggestedElsewhere[];
  knownReps: ReadonlySet<string>;
  act: (a: LinkAction) => void;
}) {
  const closest = closestClaimTarget(u, open, free, suggested, knownReps);
  const preferred = preferredClaimTarget(u, open, free, suggested, knownReps);
  const [picked, setPicked] = useState<string | null>(preferred);
  // A pick that no longer exists (claimed meanwhile, data refreshed) falls back to the preferred one.
  const key = resolveClaimTarget(picked, open, free, suggested) ? picked : preferred;
  const target = resolveClaimTarget(key, open, free, suggested);
  if (!target || !key) return null;
  const active = free.filter((d) => d.status === 'active');
  const stopped = free.filter((d) => d.status !== 'active');
  const freeLabel = (d: NoFeedDestination) =>
    `${d.channel ? `channel ${d.channel} · ` : ''}${d.reason === 'closed' ? `links ended${d.linksEnded ? ` ${d.linksEnded}` : ''}` : d.reason === 'unlinked' ? 'unlinked' : 'no feed in Airman'}`;
  return (
    <span className="match-actions claim">
      <select
        className="chip"
        aria-label={`Destination to claim feed ${u.feedId} for`}
        value={key}
        onChange={(e) => setPicked(e.target.value)}
      >
        {open.length ? (
          <optgroup label="Open registrations">
            {open.map((o) => (
              <option key={registrationKey(o.id)} value={registrationKey(o.id)}>
                {o.destinationName ?? o.destinationId} · pushed feed {o.feedId}
              </option>
            ))}
          </optgroup>
        ) : null}
        {active.length ? (
          <optgroup label="Destinations with no live link">
            {active.map((d) => (
              <option key={destinationKey(d.destinationId)} value={destinationKey(d.destinationId)}>
                {d.destinationName ?? d.destinationId} · {freeLabel(d)}
              </option>
            ))}
          </optgroup>
        ) : null}
        {stopped.length ? (
          <optgroup label="Stopped in Airman, no live link">
            {stopped.map((d) => (
              <option key={destinationKey(d.destinationId)} value={destinationKey(d.destinationId)}>
                {d.destinationName ?? d.destinationId} · stopped · {freeLabel(d)}
              </option>
            ))}
          </optgroup>
        ) : null}
        {suggested.length ? (
          <optgroup label="Suggested to another feed">
            {suggested.map((l) => (
              <option key={suggestedKey(l.id)} value={suggestedKey(l.id)}>
                {l.destinationName ?? l.destinationId} · suggested feed {l.feedId}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
      <button
        className="small"
        onClick={() =>
          act(
            target.kind === 'registration'
              ? {
                  linkId: target.reg.id,
                  action: 'assign',
                  accountId: u.accountId,
                  externalId: u.feedId,
                  updatedAt: target.reg.updatedAt,
                }
              : target.kind === 'suggested'
                ? {
                    linkId: target.link.id,
                    action: 'assign',
                    accountId: u.accountId,
                    externalId: u.feedId,
                    updatedAt: target.link.updatedAt,
                  }
                : {
                    action: 'claim',
                    destinationId: target.dest.destinationId,
                    accountId: u.accountId,
                    externalId: u.feedId,
                  },
          )
        }
      >
        Match
      </button>
      {closest && closest.key === key ? (
        <span
          className="count claim-why"
          data-closest
          title="why this destination is preselected: the closest match in Airman's list"
        >
          closest: {closest.why}
        </span>
      ) : closest ? null : (
        <span
          className="count claim-why"
          data-closest="none"
          title="no registration, channel, URL or name in Airman's list carries this feed number"
        >
          no close match in Airman - pick the destination yourself
        </span>
      )}
    </span>
  );
}

/**
 * The dominant-geo label is a label; this check is the check. A destination
 * asking for GB against a feed with 6,393 searches and none of them GB is a
 * bad match, and the dominant-geo label alone would call it "DE" and look
 * reasonable. No checks means the destination declared no geo — which really
 * means ANY geo; a feed with no geo just means we have no country for it.
 * Different facts, so no pill rather than a false reassurance. A multi-country
 * destination gets one verdict per declared country: served ones in one pill,
 * missing ones in a warning pill.
 */
function GeoCheck({
  checks,
  total,
  feedGeo,
}: {
  checks: GeoCheckRow[];
  total: number;
  feedGeo?: string | null;
}) {
  if (checks.length === 0) return null;
  // No rows at all is not a geo mismatch: nothing has been crawled for this
  // feed yet, and "no DE · 0 / 0" read as one (P2f).
  if (total <= 0) {
    return (
      <span
        className="pill warn"
        title="no stats rows for this feed in the lookback — first crawl after it goes live fills this"
      >
        feed has no stats yet
      </span>
    );
  }
  // Rows with no country cannot fail a country check (review 2026-09-18: an email report read as "no US").
  if (feedGeo === null) {
    return (
      <span
        className="pill"
        title="the feed's rows carry no country, so the destination's geo cannot be checked against them"
      >
        no country in the stats
      </span>
    );
  }
  const served = checks.filter((c) => c.searches > 0);
  const missing = checks.filter((c) => c.searches === 0).map((c) => c.geo);
  const title = checks
    .map((c) => `${fmtInt(c.searches)} of the feed's ${fmtInt(total)} searches are ${c.geo}`)
    .join('; ');
  return (
    <>
      {served.length ? (
        <span className="pill ok" title={title}>
          {served.map((c) => `${c.geo} ${fmtInt(c.searches)}`).join(', ')} / {fmtInt(total)}
        </span>
      ) : null}
      {missing.length ? (
        <span className="pill warn" title={title}>
          no {missing.join(', ')} · 0 / {fmtInt(total)}
        </span>
      ) : null}
    </>
  );
}

/**
 * The seven facts, fixed order, each as the pair of values the person matches
 * by eye - Airman's on the left, the account's or the feed's on the right -
 * with a title that says what was compared. Red where they differ, muted
 * where there is nothing to compare yet; no marks.
 */
function FactLine({ facts }: { facts: Fact[] }) {
  return (
    <>
      {facts.map((f) => (
        <span key={f.key} className={'fact ' + f.state} title={f.title} data-fact={f.key}>
          <span className="k">{f.label}</span> <span className="v">{f.left}</span>
          {f.right != null ? (
            <>
              {' '}
              <span className="arrow">→</span> <span className="v">{f.right}</span>
            </>
          ) : null}
        </span>
      ))}
    </>
  );
}

/** The auto-confirmed pill's tooltip: the numbers the daily sweep read (matching/corroborate.ts). */
function autoConfirmTip(a: NonNullable<MatchingLink['autoConfirm']>): string {
  const n = (x: number | null) => (x == null ? '—' : x.toFixed(2));
  return (
    `confirmed by the daily sweep on ${a.at.slice(0, 10)} (rule ${a.rule}): ` +
    `the feed reported on ${a.overlapDays} of the ${a.ourDays} days we sent, first on ${a.firstFeed ?? '—'} (we started ${a.firstOurs ?? '—'}); ` +
    `daily correlation ${n(a.corr)}, ratio variation ${n(a.ratioCv)}` +
    (a.groupLinks?.length ? `; scored together with ${a.groupLinks.length - 1} other link(s) on this feed` : '')
  );
}
